// Hook de "long-press" (segurar 1.5s) pra disparar a mesma ação do botão
// direito do mouse — pensado pra quem usa touch ou notebook sem mouse
// secundário. Pedido 2026-05-24.
//
// Como combina com dnd-kit:
//   - dnd-kit registra `onPointerDown` no elemento via `{...listeners}`
//   - Esse hook também escuta pointerdown — mas com setTimeout
//   - Se o aluno MOVER > 6px antes do timer disparar → cancela (vira drag)
//   - Se PASSAR 1.5s sem mover → dispara o callback (= abre menu)
//   - Pointerup/cancel SEMPRE limpa o timer
//
// Uso:
//   const longPress = useLongPress((x, y) => openContextMenuAt(x, y, items));
//   <div onPointerDown={longPress.onPointerDown} ...
//        onPointerMove={longPress.onPointerMove}
//        onPointerUp={longPress.onPointerUp}
//        onPointerCancel={longPress.onPointerUp}>
//
// IMPORTANTE: quando combinado com listeners do dnd-kit, faça um wrapper:
//   onPointerDown={(e) => { dndListeners.onPointerDown?.(e); longPress.onPointerDown(e); }}
//   senão o spread `{...listeners}` sobrescreve seus handlers.

import { useRef } from "preact/hooks";

export interface LongPressHandlers {
  onPointerDown: (e: PointerEvent) => void;
  onPointerMove: (e: PointerEvent) => void;
  onPointerUp: (e?: PointerEvent) => void;
}

export function useLongPress(
  callback: (clientX: number, clientY: number, originalEvent: PointerEvent) => void,
  opts: { ms?: number; moveTolerance?: number } = {}
): LongPressHandlers {
  const { ms = 1500, moveTolerance = 6 } = opts;
  const timerRef = useRef<number | null>(null);
  const startRef = useRef<{ x: number; y: number; ev: PointerEvent } | null>(null);

  function clear() {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startRef.current = null;
  }

  function onPointerDown(e: PointerEvent) {
    // Ignora botões secundários (direito/middle) — esses já abrem context menu
    // nativamente via onContextMenu. Long-press é só pro botão primário.
    if (e.button !== 0 && e.button !== undefined) return;
    startRef.current = { x: e.clientX, y: e.clientY, ev: e };
    timerRef.current = window.setTimeout(() => {
      const s = startRef.current;
      timerRef.current = null;
      if (s) {
        callback(s.x, s.y, s.ev);
      }
      startRef.current = null;
    }, ms);
  }

  function onPointerMove(e: PointerEvent) {
    const s = startRef.current;
    if (!s) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (dx * dx + dy * dy > moveTolerance * moveTolerance) {
      clear();
    }
  }

  function onPointerUp(_e?: PointerEvent) {
    clear();
  }

  return { onPointerDown, onPointerMove, onPointerUp };
}
